import React, { useState, useEffect, useRef } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import apiClient from '../utils/apiClient';
import logger from '../utils/logger';
import { ReactComponent as ReplyIcon } from '../images/icons/reply.svg';
import { ReactComponent as LikeIcon } from '../images/icons/like.svg';
import ConfirmationModal from '../components/ConfirmationModal';
import '../styles/FeatureRequests.css';

const FeatureRequests = () => {
    const { user, isAuthenticated } = useFirebase();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [formValues, setFormValues] = useState({
        title: '',
        description: '',
        username: ''
    });
    const [editingRequest, setEditingRequest] = useState(null);
    const [editorOpen, setEditorOpen] = useState(false);
    const [expandedRequestId, setExpandedRequestId] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    // Change from single replyContent state to a map of requestId -> content
    const [repliesOpen, setRepliesOpen] = useState({});
    const [replyContents, setReplyContents] = useState({});
    const [editingReply, setEditingReply] = useState(null);
    const [replyErrors, setReplyErrors] = useState({});
    // State for confirmation modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState(null);
    const [deleteTargetType, setDeleteTargetType] = useState(null); // 'request' or 'reply'
    const [deleteReplyRequestId, setDeleteReplyRequestId] = useState(null); // Used only when deleting a reply

    // Load username from localStorage or profile
    useEffect(() => {
        const loadUsername = async () => {
            // First check localStorage
            const storedUsername = localStorage.getItem('username');
            
            if (storedUsername) {
                // Use the stored username
                setFormValues(prev => ({
                    ...prev,
                    username: storedUsername
                }));
            } else if (user && user.displayName) {
                // Fall back to Firebase display name
                setFormValues(prev => ({
                    ...prev,
                    username: user.displayName
                }));
                // Save for next time
                localStorage.setItem('username', user.displayName);
            } else {
                // Try to fetch from profile
                try {
                    const response = await apiClient.get('/api/profile'); // Reverted
                    
                    // Handle Response object // Reverted
                    let profileData; // Reverted
                    if (response instanceof Response) { // Reverted
                        if (!response.ok) { // Reverted
                            throw new Error(`Failed to get profile: ${response.status} ${response.statusText}`); // Reverted
                        }
                        profileData = await response.json(); // Reverted
                    } else { // Reverted
                        profileData = response; // Reverted
                    }
                    
                    if (profileData && profileData.username) {
                        setFormValues(prev => ({
                            ...prev,
                            username: profileData.username
                        }));
                        // Save for next time
                        localStorage.setItem('username', profileData.username);
                    }
                } catch (error) {
                    logger.error('Error fetching profile for username:', error);
                }
            }
        };
        
        if (isAuthenticated) {
            loadUsername();
        }
    }, [isAuthenticated, user]);

    // Check if the user is an admin
    useEffect(() => {
        const checkAdminStatus = async () => {
            try {
                if (!isAuthenticated) return;
                
                const response = await apiClient.get('/api/admin/check-admin'); // Reverted
                
                // Handle Response object // Reverted
                let data; // Reverted
                if (response instanceof Response) { // Reverted
                    if (!response.ok) { // Reverted
                        throw new Error(`Failed to check admin status: ${response.status} ${response.statusText}`); // Reverted
                    }
                    data = await response.json(); // Reverted
                } else { // Reverted
                    data = response; // Reverted
                }
                
                setIsAdmin(data.isAdmin);
                
                // Save admin status in localStorage for quick access
                if (data.isAdmin) {
                    localStorage.setItem('isAdmin', 'true');
                }
            } catch (error) {
                logger.error('Error checking admin status:', error);
                setIsAdmin(false);
            }
        };
        
        // Check if we already know user is admin from localStorage
        if (localStorage.getItem('isAdmin') === 'true') {
            setIsAdmin(true);
        } else {
            checkAdminStatus();
        }
    }, [isAuthenticated]);

    // Fetch feature requests
    useEffect(() => {
        const fetchRequests = async () => {
            try {
                setLoading(true);
                const response = await apiClient.get('/api/feature-requests'); // Reverted
                
                // Add detailed debugging
                logger.debug('Feature requests API response:', response); // Reverted
                
                let responseData; // Reverted
                
                // Check if response is a Response object (needs to be parsed) // Reverted
                if (response instanceof Response) { // Reverted
                    logger.debug('Response is a Response object, parsing JSON data'); // Reverted
                    responseData = await response.json(); // Reverted
                    logger.debug('Parsed JSON data:', responseData); // Reverted
                } else { // Reverted
                    // Response is already parsed data // Reverted
                    responseData = response; // Reverted
                }
                
                // Now check the responseData format
                if (Array.isArray(responseData)) {
                    logger.debug(`Setting requests state with array of ${responseData.length} items`);
                    setRequests(responseData);
                    setError(null);
                } else if (responseData && typeof responseData === 'object') {
                    // Handle object response with possible data property
                    let requestsData;
                    
                    if (Array.isArray(responseData.data)) {
                        requestsData = responseData.data;
                    } else if (responseData.requests && Array.isArray(responseData.requests)) {
                        requestsData = responseData.requests;
                    } else if (responseData.featureRequests && Array.isArray(responseData.featureRequests)) {
                        requestsData = responseData.featureRequests;
                    } else {
                        // Last resort: look for any array property in the response
                        const arrayProps = Object.keys(responseData).filter(key => Array.isArray(responseData[key]));
                        if (arrayProps.length > 0) {
                            logger.debug(`Found array property: ${arrayProps[0]} with ${responseData[arrayProps[0]].length} items`);
                            requestsData = responseData[arrayProps[0]];
                        } else {
                            logger.error('Response data does not contain any arrays:', responseData);
                            throw new Error('Could not find feature requests array in API response');
                        }
                    }
                    
                    if (Array.isArray(requestsData)) {
                        logger.debug(`Setting requests state with array of ${requestsData.length} items`);
                        setRequests(requestsData);
                        setError(null);
                    } else {
                        throw new Error('Failed to extract feature requests array from response');
                    }
                } else {
                    throw new Error(`Unexpected response format: ${typeof responseData}`);
                }
            } catch (error) {
                logger.error('Error fetching feature requests:', error);
                setError('Failed to load feature requests. Please try again later.');
                // Ensure requests is always an array even on error
                setRequests([]);
            } finally {
                setLoading(false);
            }
        };
        
        if (isAuthenticated) {
            fetchRequests();
        }
    }, [isAuthenticated]);

    // Effect to check which descriptions are actually truncated
    useEffect(() => {
        if (requests.length > 0) {
            // Wait for DOM to update
            setTimeout(() => {
                requests.forEach(request => {
                    const descriptionEl = document.querySelector(`.feature-requests-item[data-id="${request._id}"] .feature-requests-description`);
                    const readMoreBtn = document.getElementById(`read-more-${request._id}`);
                    
                    if (descriptionEl && readMoreBtn) {
                        // Check if text overflows or line breaks make content taller than container
                        const isOverflowing = descriptionEl.scrollHeight > descriptionEl.clientHeight || 
                                             descriptionEl.scrollWidth > descriptionEl.clientWidth;
                        
                        // Only show the button if the text is actually truncated
                        if (!request.isPinned && isOverflowing) {
                            readMoreBtn.style.display = 'inline-block';
                        } else {
                            readMoreBtn.style.display = 'none';
                        }
                    }
                });
            }, 100);
        }
    }, [requests, expandedRequestId]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // Validate input based on field
        let error = '';
        if (name === 'title' && value.length > 100) {
            error = 'Title cannot exceed 100 characters';
        } else if (name === 'description' && value.length > 1000) {
            error = 'Description cannot exceed 1000 characters';
        }
        
        // Update form errors
        setFormErrors({
            ...formErrors,
            [name]: error
        });
        
        // Update form values
        if (editingRequest) {
            setEditingRequest({
                ...editingRequest,
                [name]: value
            });
        } else {
            setFormValues({
                ...formValues,
                [name]: value
            });
        }
    };

    // Submit a new feature request
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate form before submission
        const newErrors = {};
        const dataToValidate = editingRequest || formValues;
        
        if (!dataToValidate.title) {
            newErrors.title = 'Title is required';
        } else if (dataToValidate.title.length > 100) {
            newErrors.title = 'Title cannot exceed 100 characters';
        }
        
        if (!dataToValidate.description) {
            newErrors.description = 'Description is required';
        } else if (dataToValidate.description.length > 1000) {
            newErrors.description = 'Description cannot exceed 1000 characters';
        }
        
        // If there are errors, update state and stop submission
        if (Object.keys(newErrors).length > 0) {
            setFormErrors(newErrors);
            return;
        }
        
        try {
            if (editingRequest) {
                // Update existing request
                const updatedRequest = await apiClient.put(`/api/feature-requests/${editingRequest._id}`, {
                    title: editingRequest.title,
                    description: editingRequest.description
                });
                
                // Ensure updatedRequest is valid before updating state
                if (updatedRequest && updatedRequest._id) {
                    // Update the requests list
                    setRequests(prevRequests => {
                        if (!Array.isArray(prevRequests)) return [updatedRequest];
                        return prevRequests.map(req => 
                            req._id === updatedRequest._id ? updatedRequest : req
                        );
                    });
                    
                    // Close the editor and reset
                    setEditingRequest(null);
                    setEditorOpen(false);
                    setFormErrors({});
                }
            } else {
                // Get username from localStorage
                const username = localStorage.getItem('username') || user.displayName || 'User';
                
                // Create new request
                const newRequest = await apiClient.post('/api/feature-requests', {
                    title: formValues.title,
                    description: formValues.description,
                    username: username
                });
                
                // Ensure newRequest is valid before updating state
                if (newRequest && newRequest._id) {
                    // Add to the requests list
                    setRequests(prevRequests => {
                        if (!Array.isArray(prevRequests)) return [newRequest];
                        return [newRequest, ...prevRequests];
                    });
                    
                    // Clear the form
                    setFormValues({
                        title: '',
                        description: '',
                        username: username // Keep the username
                    });
                    
                    // Close the editor
                    setEditorOpen(false);
                    setFormErrors({});
                }
            }
        } catch (error) {
            logger.error('Error submitting feature request:', error);
            setError('Failed to submit feature request. Please try again later.');
        }
    };

    // Handle upvoting a request
    const handleUpvote = async (requestId) => {
        try {
            const updatedRequest = await apiClient.post(`/api/feature-requests/${requestId}/upvote`);
            
            // Ensure updatedRequest has the expected fields before updating state
            if (updatedRequest && updatedRequest._id) {
                // Update the requests list with the returned data
                setRequests(prevRequests => {
                    if (!Array.isArray(prevRequests)) return [updatedRequest];
                    return prevRequests.map(req => 
                        req._id === updatedRequest._id ? updatedRequest : req
                    );
                });
            }
        } catch (error) {
            logger.error('Error upvoting feature request:', error);
            setError('Failed to upvote feature request. Please try again later.');
        }
    };

    // Handle the Delete Request button click
    const handleRequestDeleteClick = (requestId) => {
        setDeleteTargetId(requestId);
        setDeleteTargetType('request');
        setShowDeleteModal(true);
    };

    // Handle the Delete Reply button click
    const handleReplyDeleteClick = (requestId, replyId) => {
        setDeleteTargetId(replyId);
        setDeleteReplyRequestId(requestId);
        setDeleteTargetType('reply');
        setShowDeleteModal(true);
    };

    // Handle confirmation modal close
    const handleCloseModal = () => {
        setShowDeleteModal(false);
        setDeleteTargetId(null);
        setDeleteTargetType(null);
        setDeleteReplyRequestId(null);
    };

    // Handle actual deletion after confirmation
    const handleConfirmDelete = async () => {
        try {
            if (deleteTargetType === 'request' && deleteTargetId) {
                await apiClient.delete(`/api/feature-requests/${deleteTargetId}`);
                
                // Remove from the requests list
                setRequests(prevRequests => {
                    if (!Array.isArray(prevRequests)) return [];
                    return prevRequests.filter(req => req._id !== deleteTargetId);
                });
            } else if (deleteTargetType === 'reply' && deleteTargetId && deleteReplyRequestId) {
                await apiClient.delete(`/api/feature-requests/${deleteReplyRequestId}/replies/${deleteTargetId}`);
                
                // Update the requests list by removing the deleted reply
                setRequests(prevRequests => {
                    if (!Array.isArray(prevRequests)) return [];
                    
                    return prevRequests.map(req => {
                        if (req._id === deleteReplyRequestId) {
                            const replies = Array.isArray(req.replies) ? req.replies : [];
                            return {
                                ...req,
                                replies: replies.filter(reply => reply._id !== deleteTargetId)
                            };
                        }
                        return req;
                    });
                });
            }
            
            // Close the modal
            handleCloseModal();
        } catch (error) {
            logger.error(`Error deleting ${deleteTargetType}:`, error);
            setError(`Failed to delete ${deleteTargetType}. Please try again later.`);
            handleCloseModal();
        }
    };

    // Original delete request function (modified to use the modal)
    const handleDelete = (requestId) => {
        handleRequestDeleteClick(requestId);
    };

    // Original delete reply function (modified to use the modal)
    const handleDeleteReply = (requestId, replyId) => {
        handleReplyDeleteClick(requestId, replyId);
    };

    // Pin or unpin a request (admin only)
    const handlePin = async (requestId) => {
        if (!isAdmin) return;
        
        try {
            const updatedRequest = await apiClient.post(`/api/feature-requests/${requestId}/pin`);
            
            // Ensure updatedRequest is valid before updating state
            if (updatedRequest && updatedRequest._id) {
                // Update the requests list safely
                setRequests(prevRequests => {
                    if (!Array.isArray(prevRequests)) return [updatedRequest];
                    return prevRequests.map(req => 
                        req._id === updatedRequest._id ? updatedRequest : req
                    );
                });
            }
        } catch (error) {
            logger.error('Error pinning/unpinning feature request:', error);
            setError('Failed to pin/unpin feature request. Please try again later.');
        }
    };

    // Edit a request
    const handleEdit = (request) => {
        setEditingRequest(request);
        setEditorOpen(true);
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setEditingRequest(null);
        setEditorOpen(false);
    };

    const toggleDescription = (requestId) => {
        setExpandedRequestId(expandedRequestId === requestId ? null : requestId);
    };

    // Toggle replies section
    const toggleReplies = (requestId) => {
        setRepliesOpen(prev => ({
            ...prev,
            [requestId]: !prev[requestId]
        }));
    };

    // Handle reply input changes - update to use requestId for tracking separate content
    const handleReplyChange = (e, requestId) => {
        const { value } = e.target;
        
        // Validate reply content
        let error = '';
        if (value.length > 500) {
            error = 'Reply cannot exceed 500 characters';
        }
        
        // Update errors
        setReplyErrors({
            ...replyErrors,
            [requestId]: error
        });
        
        // Update content based on whether we're editing or creating
        if (editingReply) {
            setEditingReply({
                ...editingReply,
                content: value
            });
        } else {
            // Use the requestId to maintain separate content for each reply field
            setReplyContents(prev => ({
                ...prev,
                [requestId]: value
            }));
        }
    };

    // Submit a new reply - update to use requestId for getting content
    const handleReplySubmit = async (requestId) => {
        const content = replyContents[requestId] || '';
        
        // Validate reply before submission
        if (!content.trim()) {
            setReplyErrors({
                ...replyErrors,
                [requestId]: 'Reply content is required'
            });
            return;
        }
        
        if (content.length > 500) {
            setReplyErrors({
                ...replyErrors,
                [requestId]: 'Reply cannot exceed 500 characters'
            });
            return;
        }
        
        try {
            // Get username from localStorage
            const username = localStorage.getItem('username') || user.displayName || 'User';
            
            const newReply = await apiClient.post(`/api/feature-requests/${requestId}/replies`, {
                content,
                username
            });
            
            // Check that newReply is valid before updating state
            if (newReply && newReply._id) {
                // Update the requests list with the new reply
                setRequests(prevRequests => {
                    if (!Array.isArray(prevRequests)) return [];
                    
                    return prevRequests.map(req => {
                        if (req._id === requestId) {
                            const currentReplies = Array.isArray(req.replies) ? req.replies : [];
                            return {
                                ...req,
                                replies: [...currentReplies, newReply]
                            };
                        }
                        return req;
                    });
                });
                
                // Clear the reply form for this specific request
                setReplyContents(prev => ({
                    ...prev,
                    [requestId]: ''
                }));
                setReplyErrors(prev => ({
                    ...prev,
                    [requestId]: ''
                }));
            }
        } catch (error) {
            logger.error('Error submitting reply:', error);
            setError('Failed to submit reply. Please try again later.');
        }
    };

    // Edit a reply
    const handleEditReply = (requestId, reply) => {
        setEditingReply({
            requestId,
            ...reply
        });
    };

    // Cancel editing reply
    const handleCancelEditReply = () => {
        setEditingReply(null);
        setReplyErrors({});
    };

    // Update a reply
    const handleUpdateReply = async () => {
        if (!editingReply) return;
        
        // Validate reply content
        if (!editingReply.content.trim()) {
            setReplyErrors({
                ...replyErrors,
                content: 'Reply content is required'
            });
            return;
        }
        
        if (editingReply.content.length > 500) {
            setReplyErrors({
                ...replyErrors,
                content: 'Reply cannot exceed 500 characters'
            });
            return;
        }
        
        try {
            const updatedReply = await apiClient.put(
                `/api/feature-requests/${editingReply.requestId}/replies/${editingReply._id}`,
                { content: editingReply.content }
            );
            
            // Ensure updatedReply is valid before updating state
            if (updatedReply && updatedReply._id) {
                // Update the requests list with the updated reply
                setRequests(prevRequests => {
                    if (!Array.isArray(prevRequests)) return [];
                    
                    return prevRequests.map(req => {
                        if (req._id === editingReply.requestId) {
                            const replies = Array.isArray(req.replies) ? req.replies : [];
                            return {
                                ...req,
                                replies: replies.map(reply => 
                                    reply._id === updatedReply._id ? updatedReply : reply
                                )
                            };
                        }
                        return req;
                    });
                });
                
                // Clear editing state
                setEditingReply(null);
                setReplyErrors({});
            }
        } catch (error) {
            logger.error('Error updating reply:', error);
            setError('Failed to update reply. Please try again later.');
        }
    };

    // Like/unlike a reply
    const handleLikeReply = async (requestId, replyId) => {
        try {
            const updatedReply = await apiClient.post(`/api/feature-requests/${requestId}/replies/${replyId}/like`);
            
            // Ensure updatedReply is valid before updating state
            if (updatedReply && updatedReply._id) {
                // Update the requests list with the updated reply
                setRequests(prevRequests => {
                    if (!Array.isArray(prevRequests)) return [];
                    
                    return prevRequests.map(req => {
                        if (req._id === requestId) {
                            // Ensure replies is an array
                            const currentReplies = Array.isArray(req.replies) ? req.replies : [];
                            
                            return {
                                ...req,
                                replies: currentReplies.map(reply => 
                                    reply._id === updatedReply._id ? updatedReply : reply
                                )
                            };
                        }
                        return req;
                    });
                });
            }
        } catch (error) {
            logger.error('Error liking/unliking reply:', error);
            setError('Failed to like/unlike reply. Please try again later.');
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="feature-requests-container">
                <h1 className="page-title">Feature Requests</h1>
                <div className="feature-requests-not-authenticated">
                    <p>Please sign in to view and submit feature requests.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="feature-requests-container">
            <h1 className="page-title">Feature Requests</h1>
            
            {error && <div className="feature-requests-error">{error}</div>}
            
            <div className="feature-requests-content">
                <div className="feature-requests-controls">
                    {!editorOpen ? (
                        <button 
                            className="feature-requests-new-button"
                            onClick={() => setEditorOpen(true)}
                        >
                            Submit New Request
                        </button>
                    ) : (
                        <div className="feature-requests-form-container">
                            <form className="feature-requests-form" onSubmit={handleSubmit}>
                                <h3 className="feature-requests-form-title">
                                    {editingRequest ? 'Edit Your Request' : 'New Feature Request'}
                                </h3>
                                
                                <div className="feature-requests-form-group">
                                    <label htmlFor="title">Title:</label>
                                    <input
                                        type="text"
                                        id="title"
                                        name="title"
                                        value={editingRequest ? editingRequest.title : formValues.title}
                                        onChange={handleInputChange}
                                        required
                                        className={`feature-requests-input ${formErrors.title ? 'input-error' : ''}`}
                                        placeholder="Brief title for your feature request"
                                        maxLength={100}
                                    />
                                    <div className="feature-requests-field-info">
                                        {formErrors.title && (
                                            <span className="feature-requests-error-text">{formErrors.title}</span>
                                        )}
                                        <span className="feature-requests-char-count">
                                            {(editingRequest ? editingRequest.title.length : formValues.title.length)}/100
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="feature-requests-form-group">
                                    <label htmlFor="description">Description:</label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={editingRequest ? editingRequest.description : formValues.description}
                                        onChange={handleInputChange}
                                        required
                                        className={`feature-requests-textarea ${formErrors.description ? 'input-error' : ''}`}
                                        placeholder="Describe the feature you'd like to see..."
                                        rows={5}
                                        maxLength={1000}
                                    />
                                    <div className="feature-requests-field-info">
                                        {formErrors.description && (
                                            <span className="feature-requests-error-text">{formErrors.description}</span>
                                        )}
                                        <span className="feature-requests-char-count">
                                            {(editingRequest ? editingRequest.description.length : formValues.description.length)}/1000
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="feature-requests-form-buttons">
                                    <button 
                                        type="button" 
                                        className="feature-requests-cancel-button"
                                        onClick={handleCancelEdit}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="submit" 
                                        className="feature-requests-submit-button"
                                    >
                                        {editingRequest ? 'Update' : 'Submit'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}
                </div>
                
                {loading ? (
                    <div className="feature-requests-loading">Loading feature requests...</div>
                ) : (
                    <div className="feature-requests-list">
                        {requests.length === 0 ? (
                            <div className="feature-requests-empty">
                                <p>No feature requests yet. Be the first to suggest one!</p>
                            </div>
                        ) : (
                            Array.isArray(requests) ?
                            requests.map(request => (
                                <div 
                                    key={request._id} 
                                    className={`feature-requests-item ${request.isPinned ? 'pinned' : ''}`}
                                    data-id={request._id}
                                >
                                    {request.isPinned && (
                                        <div className="feature-requests-pinned-badge">
                                            <i className="fas fa-thumbtack"></i> Pinned
                                        </div>
                                    )}
                                    
                                    <div className="feature-requests-vote">
                                        <button
                                            className={`feature-requests-upvote-button ${
                                                request.upvotes.includes(user.uid) ? 'active' : ''
                                            }`}
                                            onClick={() => handleUpvote(request._id)}
                                            aria-label="Upvote"
                                        >
                                            ▲
                                        </button>
                                        <span className="feature-requests-vote-count">
                                            {request.upvotes.length}
                                        </span>
                                    </div>
                                    
                                    <div className="feature-requests-content">
                                        <h3 className="feature-requests-title">{request.title}</h3>
                                        <div className={`feature-requests-description ${request.isPinned || expandedRequestId === request._id ? 'expanded' : 'truncated'}`}>
                                            {request.description}
                                            {!request.isPinned && request.description.length > 100 && (
                                                <button 
                                                    className="feature-requests-read-more" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleDescription(request._id);
                                                    }}
                                                    id={`read-more-${request._id}`}
                                                >
                                                    {expandedRequestId === request._id ? 'Show less' : 'Show more'}
                                                </button>
                                            )}
                                        </div>
                                        <div className="feature-requests-meta">
                                            <span className="feature-requests-author">
                                                Posted by: {request.username}
                                                {request.isAdmin && <span className="admin-badge"> (Admin)</span>}
                                            </span>
                                            <span className="feature-requests-date hidden">
                                                {new Date(request.createdAt).toLocaleDateString()}
                                            </span>
                                            
                                            {/* Comment icon with reply count */}
                                            <button
                                                className="feature-requests-replies-toggle"
                                                onClick={() => toggleReplies(request._id)}
                                                aria-label="Toggle replies"
                                            >
                                                <ReplyIcon className="reply-icon" />
                                                <span className="feature-requests-reply-count">
                                                    {request.replies ? request.replies.length : 0}
                                                </span>
                                            </button>
                                        </div>
                                        
                                        {/* Replies section */}
                                        {repliesOpen[request._id] && (
                                            <div className="feature-requests-replies-container">
                                                <h4 className="feature-requests-replies-title">Replies</h4>
                                                
                                                {/* Replies list */}
                                                <div className="feature-requests-replies-list">
                                                    {!request.replies || request.replies.length === 0 ? (
                                                        <p className="feature-requests-no-replies">No replies yet. Be the first to reply!</p>
                                                    ) : (
                                                        Array.isArray(request.replies) ? 
                                                        request.replies.map(reply => (
                                                            <div key={reply._id} className="feature-requests-reply">
                                                                <div className="feature-requests-reply-header">
                                                                    <span className="feature-requests-reply-author">
                                                                        {reply.username}
                                                                        {reply.isAdmin && <span className="admin-badge"> (Admin)</span>}
                                                                    </span>
                                                                    <span className="feature-requests-reply-date">
                                                                        {new Date(reply.createdAt).toLocaleDateString()}
                                                                    </span>
                                                                </div>
                                                                
                                                                {editingReply && editingReply._id === reply._id ? (
                                                                    <div className="feature-requests-reply-edit">
                                                                        <textarea
                                                                            className={`feature-requests-reply-textarea ${replyErrors.content ? 'input-error' : ''}`}
                                                                            value={editingReply.content}
                                                                            onChange={(e) => handleReplyChange(e, request._id)}
                                                                            placeholder="Edit your reply..."
                                                                            rows={3}
                                                                            maxLength={500}
                                                                        />
                                                                        
                                                                        <div className="feature-requests-reply-field-info">
                                                                            {replyErrors.content && (
                                                                                <span className="feature-requests-error-text">{replyErrors.content}</span>
                                                                            )}
                                                                            <span className="feature-requests-char-count">
                                                                                {editingReply.content.length}/500
                                                                            </span>
                                                                        </div>
                                                                        
                                                                        <div className="feature-requests-reply-buttons">
                                                                            <button
                                                                                className="feature-requests-reply-cancel-button"
                                                                                onClick={handleCancelEditReply}
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                            <button
                                                                                className="feature-requests-reply-update-button"
                                                                                onClick={handleUpdateReply}
                                                                            >
                                                                                Update
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        <p className="feature-requests-reply-content">{reply.content}</p>
                                                                        
                                                                        <div className="feature-requests-reply-actions">
                                                                            <button
                                                                                className={`feature-requests-reply-like-button ${
                                                                                    reply.likes.includes(user.uid) ? 'active' : ''
                                                                                }`}
                                                                                onClick={() => handleLikeReply(request._id, reply._id)}
                                                                                aria-label="Like reply"
                                                                            >
                                                                                <LikeIcon className="like-icon" />
                                                                                <span className="feature-requests-reply-likes-count">
                                                                                    {reply.likes ? reply.likes.length : 0}
                                                                                </span>
                                                                            </button>
                                                                            
                                                                            {(isAdmin || reply.userId === user.uid) && (
                                                                                <>
                                                                                    <button
                                                                                        className="feature-requests-reply-edit-button"
                                                                                        onClick={() => handleEditReply(request._id, reply)}
                                                                                        aria-label="Edit reply"
                                                                                    >
                                                                                        Edit
                                                                                    </button>
                                                                                    <button
                                                                                        className="feature-requests-reply-delete-button"
                                                                                        onClick={() => handleDeleteReply(request._id, reply._id)}
                                                                                        aria-label="Delete reply"
                                                                                    >
                                                                                        Delete
                                                                                    </button>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    </>
                                                                )}
                                                            </div>
                                                        ))
                                                        : <div className="feature-requests-error">Error loading replies. Please refresh the page.</div>
                                                    )}
                                                </div>
                                                
                                                {/* Add new reply form - updated to use requestId for handling changes */}
                                                <div className="feature-requests-reply-form">
                                                    <textarea
                                                        className={`feature-requests-reply-textarea ${replyErrors[request._id] ? 'input-error' : ''}`}
                                                        value={replyContents[request._id] || ''}
                                                        onChange={(e) => handleReplyChange(e, request._id)}
                                                        placeholder="Leave a reply..."
                                                        rows={3}
                                                        maxLength={500}
                                                    />
                                                    
                                                    <div className="feature-requests-reply-field-info">
                                                        {replyErrors[request._id] && (
                                                            <span className="feature-requests-error-text">{replyErrors[request._id]}</span>
                                                        )}
                                                        <span className="feature-requests-char-count">
                                                            {(replyContents[request._id]?.length || 0)}/500
                                                        </span>
                                                    </div>
                                                    
                                                    <button
                                                        className="feature-requests-reply-submit-button"
                                                        onClick={() => handleReplySubmit(request._id)}
                                                    >
                                                        Post
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="feature-requests-actions">
                                        {(isAdmin || request.userId === user.uid) && (
                                            <>
                                                <button
                                                    className="feature-requests-edit-button"
                                                    onClick={() => handleEdit(request)}
                                                    aria-label="Edit"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    className="feature-requests-delete-button"
                                                    onClick={() => handleDelete(request._id)}
                                                    aria-label="Delete"
                                                >
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                        
                                        {isAdmin && (
                                            <button
                                                className="feature-requests-pin-button"
                                                onClick={() => handlePin(request._id)}
                                                aria-label={request.isPinned ? 'Unpin' : 'Pin'}
                                            >
                                                {request.isPinned ? 'Unpin' : 'Pin'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))
                            : <div className="feature-requests-error">Error loading feature requests. Please refresh the page.</div>
                        )}
                    </div>
                )}
            </div>
            
            {/* Delete Confirmation Modal using the component */}
            <ConfirmationModal
                isOpen={showDeleteModal}
                onClose={handleCloseModal}
                onConfirm={handleConfirmDelete}
                title="Confirm Deletion"
                message={
                    <>
                        <p>
                            {deleteTargetType === 'request' 
                                ? 'Are you sure you want to delete this feature request?' 
                                : 'Are you sure you want to delete this reply?'}
                        </p>
                        <p>This action cannot be undone.</p>
                    </>
                }
                confirmText="Delete"
                cancelText="Cancel"
                className="feature-requests"
            />
        </div>
    );
};

export default FeatureRequests; 